import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  FileText, Plus, Settings, Eye, Download, Trash2, CheckCircle, Clock,
  Loader2, RefreshCw, TrendingUp, TrendingDown, DollarSign, Users,
  Target, BarChart3, PieChart, Sparkles, Building2, Calendar, Send,
  FileBarChart, Layers, AlertTriangle, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart as RePieChart, Pie, Cell } from 'recharts';
import api from '../api';

const REPORT_TYPES = [
  { value: 'budget_monthly', label: 'Monthly Budget Report', icon: DollarSign },
  { value: 'budget_yearly', label: 'Yearly Budget Report', icon: Calendar },
  { value: 'campaign_performance', label: 'Campaign Performance', icon: Target },
  { value: 'demand_planning', label: 'Demand Planning', icon: BarChart3 },
  { value: 'executive_summary', label: 'Executive Summary', icon: FileBarChart }
];

const AUDIENCE_LEVELS = [
  { value: 'staff', label: 'Staff', description: 'Detailed operational view' },
  { value: 'management', label: 'Management', description: 'Tactical insights' },
  { value: 'senior_management', label: 'Senior Management', description: 'Strategic overview' },
  { value: 'board', label: 'Board', description: 'High-level summary' }
];

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: FileText },
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  published: { label: 'Published', color: 'bg-blue-100 text-blue-700', icon: Send }
};

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const MarketingReportsPage = () => {
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('reports');
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [letterhead, setLetterhead] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showLetterheadDialog, setShowLetterheadDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Form state
  const [reportForm, setReportForm] = useState({
    title: '',
    report_type: 'campaign_performance',
    audience_level: 'management',
    template_id: '',
    period_start: '',
    period_end: '',
    campaign_ids: [],
    include_ai_analysis: true,
    notes: ''
  });

  const [letterheadForm, setLetterheadForm] = useState({
    company_name: '',
    logo_url: '',
    address_line1: '',
    address_line2: '',
    phone: '',
    email: '',
    website: '',
    tagline: '',
    primary_color: '#1e40af',
    secondary_color: '#64748b'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportsRes, templatesRes, campaignsRes, letterheadRes, dashboardRes] = await Promise.all([
        api.get('/marketing-reports/reports', { params: { limit: 50 } }),
        api.get('/marketing-reports/templates'),
        api.get('/marketing/campaigns', { params: { limit: 100 } }),
        api.get('/marketing-reports/letterhead'),
        api.get('/marketing-reports/dashboard')
      ]);
      
      setReports(reportsRes.data?.reports || []);
      setTemplates(templatesRes.data?.templates || []);
      setCampaigns(campaignsRes.data || []);
      setLetterhead(letterheadRes.data);
      setLetterheadForm(letterheadRes.data || letterheadForm);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load reports data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!reportForm.title) {
      toast.error('Report title is required');
      return;
    }

    try {
      setGenerating(true);
      const res = await api.post('/marketing-reports/generate', reportForm);
      toast.success('Report generated successfully!');
      setShowCreateDialog(false);
      setReportForm({
        title: '',
        report_type: 'campaign_performance',
        audience_level: 'management',
        template_id: '',
        period_start: '',
        period_end: '',
        campaign_ids: [],
        include_ai_analysis: true,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveLetterhead = async () => {
    try {
      await api.put('/marketing-reports/letterhead', letterheadForm);
      toast.success('Letterhead updated');
      setShowLetterheadDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save letterhead');
    }
  };

  const handleViewReport = async (report) => {
    try {
      const res = await api.get(`/marketing-reports/reports/${report.id}`);
      setSelectedReport(res.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  const handleUpdateStatus = async (reportId, status) => {
    try {
      await api.put(`/marketing-reports/reports/${reportId}/status`, null, { params: { status } });
      toast.success(`Report ${status}`);
      fetchData();
      if (selectedReport?.id === reportId) {
        setSelectedReport({ ...selectedReport, status });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.delete(`/marketing-reports/reports/${reportId}`);
      toast.success('Report deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const toggleCampaign = (campaignId) => {
    setReportForm(prev => ({
      ...prev,
      campaign_ids: prev.campaign_ids.includes(campaignId)
        ? prev.campaign_ids.filter(id => id !== campaignId)
        : [...prev.campaign_ids, campaignId]
    }));
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <ArrowUp className="h-4 w-4 text-green-600" />;
    if (variance < 0) return <ArrowDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-slate-400" />;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  return (
    <div className="space-y-6" data-testid="marketing-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileBarChart className="h-6 w-6" />
            Marketing Reports
          </h1>
          <p className="text-muted-foreground">Generate and manage formatted marketing reports</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowLetterheadDialog(true)}>
            <Building2 className="h-4 w-4 mr-2" />
            Letterhead
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reports</p>
                  <p className="text-2xl font-bold">{dashboard.stats?.total_reports || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Budget Reports</p>
                  <p className="text-2xl font-bold">{dashboard.stats?.budget_reports || 0}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Performance</p>
                  <p className="text-2xl font-bold">{dashboard.stats?.performance_reports || 0}</p>
                </div>
                <Target className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Demand Planning</p>
                  <p className="text-2xl font-bold">{dashboard.stats?.demand_reports || 0}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Templates</p>
                  <p className="text-2xl font-bold">{dashboard.stats?.templates || 0}</p>
                </div>
                <Layers className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="reports">Generated Reports</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="budget">Budget Data</TabsTrigger>
        </TabsList>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileBarChart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-semibold mb-2">No Reports Yet</h3>
                <p className="text-muted-foreground mb-4">Generate your first marketing report</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {reports.map(report => {
                const StatusIcon = STATUS_CONFIG[report.status]?.icon || FileText;
                const TypeIcon = REPORT_TYPES.find(t => t.value === report.report_type)?.icon || FileText;
                
                return (
                  <Card key={report.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <TypeIcon className="h-5 w-5 text-muted-foreground" />
                            <h3 className="font-semibold text-lg">{report.title}</h3>
                            <Badge className={STATUS_CONFIG[report.status]?.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {STATUS_CONFIG[report.status]?.label}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {AUDIENCE_LEVELS.find(a => a.value === report.audience_level)?.label}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            <span>{REPORT_TYPES.find(t => t.value === report.report_type)?.label}</span>
                            {report.period_start && (
                              <span>Period: {report.period_start} - {report.period_end || 'Present'}</span>
                            )}
                            <span>Created: {format(new Date(report.created_at), 'MMM d, yyyy')}</span>
                          </div>

                          {/* Scores */}
                          <div className="flex items-center gap-4">
                            {report.overall_score > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Overall:</span>
                                <Badge variant={report.overall_score >= 70 ? 'default' : report.overall_score >= 50 ? 'secondary' : 'destructive'}>
                                  {report.overall_score.toFixed(0)}%
                                </Badge>
                              </div>
                            )}
                            {report.ai_analysis && (
                              <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                <Sparkles className="h-3 w-3 mr-1" />
                                AI Enhanced
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleViewReport(report)}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {report.status === 'draft' && hasRole(['admin', 'marketing_manager']) && (
                            <Button variant="outline" size="sm" onClick={() => handleUpdateStatus(report.id, 'pending_review')}>
                              Submit
                            </Button>
                          )}
                          {report.status === 'pending_review' && hasRole(['admin', 'marketing_manager']) && (
                            <Button variant="default" size="sm" onClick={() => handleUpdateStatus(report.id, 'approved')}>
                              Approve
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-500"
                            onClick={() => handleDeleteReport(report.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Report Templates</CardTitle>
              <CardDescription>Pre-configured templates for different report types and audiences</CardDescription>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No templates created yet</p>
                  <Button variant="outline" size="sm" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map(template => (
                    <Card key={template.id} className="border-2">
                      <CardContent className="p-4">
                        <h4 className="font-semibold mb-2">{template.name}</h4>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Badge variant="outline">{REPORT_TYPES.find(t => t.value === template.report_type)?.label}</Badge>
                          <Badge variant="outline">{AUDIENCE_LEVELS.find(a => a.value === template.audience_level)?.label}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Budget Data Entry</CardTitle>
              <CardDescription>Enter monthly budget data for report generation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>Budget data entry coming soon</p>
                <p className="text-sm">Use the "Generate Report" button to create budget reports with existing data</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Report Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBarChart className="h-5 w-5" />
              Generate Marketing Report
            </DialogTitle>
            <DialogDescription>Create a formatted report with AI analysis</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Report Title *</Label>
                <Input
                  placeholder="Q4 2025 Marketing Performance Report"
                  value={reportForm.title}
                  onChange={(e) => setReportForm({ ...reportForm, title: e.target.value })}
                />
              </div>

              <div>
                <Label>Report Type</Label>
                <Select value={reportForm.report_type} onValueChange={(v) => setReportForm({ ...reportForm, report_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Audience Level</Label>
                <Select value={reportForm.audience_level} onValueChange={(v) => setReportForm({ ...reportForm, audience_level: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIENCE_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        <div>
                          <span className="font-medium">{level.label}</span>
                          <span className="text-xs text-muted-foreground ml-2">{level.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={reportForm.period_start}
                  onChange={(e) => setReportForm({ ...reportForm, period_start: e.target.value })}
                />
              </div>

              <div>
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={reportForm.period_end}
                  onChange={(e) => setReportForm({ ...reportForm, period_end: e.target.value })}
                />
              </div>
            </div>

            {/* Campaign Selection */}
            {reportForm.report_type === 'campaign_performance' && (
              <div>
                <Label>Select Campaigns</Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2 mt-2">
                  {campaigns.map(campaign => (
                    <div key={campaign.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={reportForm.campaign_ids.includes(campaign.id)}
                        onCheckedChange={() => toggleCampaign(campaign.id)}
                      />
                      <span className="text-sm">{campaign.name}</span>
                      <Badge variant="outline" className="text-xs capitalize">{campaign.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                checked={reportForm.include_ai_analysis}
                onCheckedChange={(v) => setReportForm({ ...reportForm, include_ai_analysis: v })}
              />
              <Label className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                Include AI Analysis & Recommendations
              </Label>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Additional context or notes..."
                value={reportForm.notes}
                onChange={(e) => setReportForm({ ...reportForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateReport} disabled={generating}>
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileBarChart className="h-4 w-4 mr-2" />
              )}
              Generate Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Report Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedReport && (
            <>
              <DialogHeader className="border-b pb-4">
                {/* Letterhead */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    {selectedReport.letterhead?.logo_url && (
                      <img src={selectedReport.letterhead.logo_url} alt="Logo" className="h-12" />
                    )}
                    <div>
                      <h2 className="text-xl font-bold" style={{ color: selectedReport.letterhead?.primary_color }}>
                        {selectedReport.letterhead?.company_name || 'Company'}
                      </h2>
                      {selectedReport.letterhead?.tagline && (
                        <p className="text-sm text-muted-foreground">{selectedReport.letterhead.tagline}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    {selectedReport.letterhead?.address_line1 && <p>{selectedReport.letterhead.address_line1}</p>}
                    {selectedReport.letterhead?.phone && <p>{selectedReport.letterhead.phone}</p>}
                    {selectedReport.letterhead?.email && <p>{selectedReport.letterhead.email}</p>}
                  </div>
                </div>
                
                <DialogTitle className="text-2xl">{selectedReport.title}</DialogTitle>
                <div className="flex items-center gap-3 mt-2">
                  <Badge className={STATUS_CONFIG[selectedReport.status]?.color}>
                    {STATUS_CONFIG[selectedReport.status]?.label}
                  </Badge>
                  <Badge variant="outline">{REPORT_TYPES.find(t => t.value === selectedReport.report_type)?.label}</Badge>
                  <Badge variant="outline">{AUDIENCE_LEVELS.find(a => a.value === selectedReport.audience_level)?.label}</Badge>
                  <span className="text-sm text-muted-foreground">
                    Generated: {format(new Date(selectedReport.created_at), 'MMMM d, yyyy')}
                  </span>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Executive Summary */}
                {selectedReport.executive_summary && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Executive Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p>{selectedReport.executive_summary}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Key Highlights & Concerns */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedReport.key_highlights?.length > 0 && (
                    <Card className="bg-green-50 border-green-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-green-800 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Key Highlights
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1">
                          {selectedReport.key_highlights.map((h, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                              {h}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                  
                  {selectedReport.key_concerns?.length > 0 && (
                    <Card className="bg-amber-50 border-amber-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          Key Concerns
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1">
                          {selectedReport.key_concerns.map((c, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                              {c}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Estimated vs Actual */}
                {selectedReport.estimated_vs_actual && Object.keys(selectedReport.estimated_vs_actual).some(k => selectedReport.estimated_vs_actual[k]?.estimated > 0) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Estimated vs Actual Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {selectedReport.estimated_vs_actual.budget?.estimated > 0 && (
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Budget</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-muted-foreground">Est: {formatCurrency(selectedReport.estimated_vs_actual.budget.estimated)}</p>
                                <p className="font-bold">{formatCurrency(selectedReport.estimated_vs_actual.budget.actual)}</p>
                              </div>
                              {getVarianceIcon(selectedReport.estimated_vs_actual.budget.variance)}
                            </div>
                          </div>
                        )}
                        {selectedReport.estimated_vs_actual.leads?.estimated > 0 && (
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Leads</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-muted-foreground">Target: {selectedReport.estimated_vs_actual.leads.estimated}</p>
                                <p className="font-bold">{selectedReport.estimated_vs_actual.leads.actual}</p>
                              </div>
                              {getVarianceIcon(selectedReport.estimated_vs_actual.leads.variance)}
                            </div>
                          </div>
                        )}
                        {selectedReport.estimated_vs_actual.sales?.estimated > 0 && (
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Sales</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-muted-foreground">Target: {formatCurrency(selectedReport.estimated_vs_actual.sales.estimated)}</p>
                                <p className="font-bold">{formatCurrency(selectedReport.estimated_vs_actual.sales.actual)}</p>
                              </div>
                              {getVarianceIcon(selectedReport.estimated_vs_actual.sales.variance)}
                            </div>
                          </div>
                        )}
                        {selectedReport.estimated_vs_actual.demand?.estimated > 0 && (
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Demand</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-muted-foreground">Est: {selectedReport.estimated_vs_actual.demand.estimated}</p>
                                <p className="font-bold">{selectedReport.estimated_vs_actual.demand.actual}</p>
                              </div>
                              {getVarianceIcon(selectedReport.estimated_vs_actual.demand.variance)}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Scores */}
                {selectedReport.overall_score > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Performance Scores</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <p className="text-3xl font-bold text-blue-700">{selectedReport.overall_score.toFixed(0)}%</p>
                          <p className="text-sm text-blue-600">Overall</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <p className="text-3xl font-bold text-green-700">{selectedReport.budget_health_score.toFixed(0)}%</p>
                          <p className="text-sm text-green-600">Budget Health</p>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                          <p className="text-3xl font-bold text-purple-700">{selectedReport.performance_score.toFixed(0)}%</p>
                          <p className="text-sm text-purple-600">Performance</p>
                        </div>
                        <div className="text-center p-4 bg-amber-50 rounded-lg">
                          <p className="text-3xl font-bold text-amber-700">{selectedReport.forecast_accuracy_score.toFixed(0)}%</p>
                          <p className="text-sm text-amber-600">Forecast Accuracy</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AI Recommendations */}
                {selectedReport.ai_recommendations?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        AI Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedReport.ai_recommendations.map((rec, i) => (
                          <div key={i} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'default' : 'secondary'}>
                                {rec.priority}
                              </Badge>
                              <span className="font-medium">{rec.area}</span>
                            </div>
                            <p className="text-sm">{rec.recommendation}</p>
                            {rec.expected_impact && (
                              <p className="text-xs text-purple-600 mt-1">Expected Impact: {rec.expected_impact}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AI Insights */}
                {selectedReport.ai_insights?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">AI Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {selectedReport.ai_insights.map((insight, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Sparkles className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>

              <DialogFooter className="border-t pt-4">
                <div className="flex items-center gap-2 w-full justify-between">
                  <div className="text-xs text-muted-foreground">
                    Report ID: {selectedReport.id}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedReport.status === 'draft' && (
                      <Button variant="outline" onClick={() => handleUpdateStatus(selectedReport.id, 'pending_review')}>
                        Submit for Review
                      </Button>
                    )}
                    {selectedReport.status === 'pending_review' && hasRole(['admin', 'marketing_manager']) && (
                      <Button onClick={() => handleUpdateStatus(selectedReport.id, 'approved')}>
                        Approve Report
                      </Button>
                    )}
                    {selectedReport.status === 'approved' && (
                      <Button onClick={() => handleUpdateStatus(selectedReport.id, 'published')}>
                        Publish Report
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
                  </div>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Letterhead Dialog */}
      <Dialog open={showLetterheadDialog} onOpenChange={setShowLetterheadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Letterhead
            </DialogTitle>
            <DialogDescription>Configure the letterhead for all generated reports</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={letterheadForm.company_name}
                onChange={(e) => setLetterheadForm({ ...letterheadForm, company_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input
                placeholder="https://..."
                value={letterheadForm.logo_url}
                onChange={(e) => setLetterheadForm({ ...letterheadForm, logo_url: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Address Line 1</Label>
                <Input
                  value={letterheadForm.address_line1}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, address_line1: e.target.value })}
                />
              </div>
              <div>
                <Label>Address Line 2</Label>
                <Input
                  value={letterheadForm.address_line2}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, address_line2: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={letterheadForm.phone}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={letterheadForm.email}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, email: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={letterheadForm.website}
                onChange={(e) => setLetterheadForm({ ...letterheadForm, website: e.target.value })}
              />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                placeholder="Your company tagline..."
                value={letterheadForm.tagline}
                onChange={(e) => setLetterheadForm({ ...letterheadForm, tagline: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={letterheadForm.primary_color}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={letterheadForm.primary_color}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, primary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Secondary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={letterheadForm.secondary_color}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, secondary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={letterheadForm.secondary_color}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, secondary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLetterheadDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveLetterhead}>Save Letterhead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingReportsPage;
